import { techCashback as content } from '~/content/tech';
import { Section } from '~/components/layout';
import { ButtonLink, SmartImage, TransactionToast } from '~/components/ui';
import { cn } from '~/lib/cn';

/**
 * "50% cashback on Claude and ChatGPT" band (Figma 1357:55335 / mobile 1380:67702).
 * Desktop: the copy sits in white over the full-bleed card photo. Mobile: the
 * photo stacks above the copy.
 *
 * The payment toasts scale with the photo: `cqw` sets 1em to one toast design
 * pixel, on the 1400px frame (the mobile frame draws them at 0.7125 of that on
 * a 353px photo).
 */
export function TechCashback({ className }: { className?: string }) {
  return (
    <Section className={className}>
      <div className="relative lg:h-[762px]">
        <div className="@container relative aspect-[353/482] w-full overflow-hidden md:aspect-[1400/762] lg:absolute lg:inset-0 lg:aspect-auto">
          <SmartImage image={content.image} className="size-full" />
          <span
            aria-hidden
            className="absolute inset-0 hidden bg-[linear-gradient(95.74deg,rgba(0,0,0,0.3)_23.94%,rgba(255,255,255,0)_97.13%)] lg:block"
          />
          <div className="absolute top-1/2 left-[18.41%] flex flex-col gap-[19.6em] text-[length:calc(100cqw/495.44)] md:top-[41.47%] md:left-[59.21%] md:text-[length:calc(100cqw/1400)]">
            {content.transactions.map((tx) => (
              <TransactionToast
                key={tx.label}
                label={tx.label}
                amount={tx.amount}
                logo={tx.logo}
                {...(tx.nsaveTile && {
                  logoSize: 43,
                  tileClassName: 'bg-[#ef0505]',
                  logoClassName: 'mix-blend-lighten',
                })}
              />
            ))}
          </div>
        </div>

        <div className="relative mt-[50px] lg:mt-0 lg:ps-10 lg:pt-[42px]">
          <div className="border-b border-black-6 pb-8 lg:w-[500px] lg:border-white">
            <h2 className="text-h3 leading-[1.16] font-semibold tracking-[-0.022em] whitespace-pre-line text-grey-1000 md:text-h2 lg:text-white">
              {content.title}
            </h2>
          </div>
          <p
            className={cn(
              'mt-9 max-w-[326px] text-[15px] leading-[1.32] tracking-[-0.03px] text-grey-900',
              'lg:mt-10 lg:max-w-[529px] lg:text-body lg:leading-[1.16] lg:font-semibold lg:text-white',
            )}>
            {content.body}
          </p>
          <ButtonLink
            href={content.cta.href}
            className="mt-[35px] max-md:!text-[14px] max-md:!tracking-[0.168px] lg:mt-[85px]">
            {content.cta.label}
          </ButtonLink>
        </div>
      </div>
    </Section>
  );
}
